import {HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {Component, computed, OnDestroy, Signal, signal, WritableSignal} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatLabel} from '@angular/material/form-field';
import {MatIcon} from '@angular/material/icon';
import {MatProgressBarModule} from '@angular/material/progress-bar';
import {MatRadioButton, MatRadioGroup} from '@angular/material/radio';
import {MatSnackBar} from '@angular/material/snack-bar';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {DeleteDialogComponent} from '@app/dialogs/object-delete-dialog/object-delete-dialog.component';
import {SubsectionScrollDirective} from '@app/directives/subsection-scroll/subsection-scroll.directive';
import {PermissionsTableComponent} from '@app/permissions/permissions-table/permissions-table.component';
import {mapUsersToPermissionRows, PermissionRow, PermissionsContext} from '@app/permissions/permissions.mapper';
import {pdeRoutes} from '@app/routing/routes';
import {AuthenticationService} from '@authentication/authentication.service';
import {
  DatasetResponse,
  DatasetRoleID,
  DatasetsService,
  DatasetUserResponse,
  FileVersionResponse,
  RelatedRecordResponse,
} from '@backend';
import {FilePublicationValidationDialogComponent} from '@forms/file-publication-validation-dialog/file-publication-validation-dialog.component';
import {IndexService, PublicDatasetFileService} from '@middleware';
import {CommonsSearchDataset} from '@models/commons-types';
import {FormlyModule} from '@ngx-formly/core';
import {LandingServiceConfigService} from '@services/config/landing-service-config.service';
import {LandingServiceBrokerService} from '@services/landing-service-broker/landing-service-broker.service';
import {SectionHeaderActions} from '@shared/components/section-header-actions/section-header-actions';
import {DatasetRoleOptions} from '@shared/labels/dataset-role';
import extractError from '@utilities/error-extractor';
import {lastValueFrom, Subscription} from 'rxjs';
import {canEnableOrganizationalAccess} from '@app/dataset/dataset-utils';

type DatasetWithPermissionEmails = DatasetResponse &
  CommonsSearchDataset & {
    availablePermissionEmails: string[];
  };

interface SyncProgress {
  isChunked?: boolean;
  isProcessingMetadata?: boolean;
  isFinalizing?: boolean;
  downloaded?: number;
  uploaded?: number;
  total?: number;
}

//512MB decision size for chunking, chosen to be ~half of tested browser maximum
const CHUNK_LIMIT = 0x20000000;
//64MB chunks, chosen completely arbitrarily
const CHUNK_SIZE = 0x04000000;

@Component({
  selector: 'app-dataset-access',
  imports: [
    FormlyModule,
    ReactiveFormsModule,
    MatRadioGroup,
    MatRadioButton,
    RouterLink,
    MatIcon,
    MatButton,
    PermissionsTableComponent,
    SubsectionScrollDirective,
    MatProgressBarModule,
  ],
  templateUrl: './dataset-access.component.html',
  styleUrls: ['./dataset-access.component.scss'],
})

export class DatasetAccessComponent implements OnDestroy, SectionHeaderActions {
  public dataset: WritableSignal<DatasetResponse & CommonsSearchDataset> = signal(undefined);
  public project: Signal<RelatedRecordResponse> = computed(
    () => this.dataset()?.associated_projects?.find(p => p.is_primary_project)?.project,
  );

  get datasetPermissionContext(): PermissionsContext {
    return {
      type: 'dataset' as const,
      id: this.dataset()?.id,
      editable: true,
      showEditButton: false,
      canEnableOrganizationalAccess: canEnableOrganizationalAccess(this.dataset()),
      syncFromPrimaryProject: computed(() => this.dataset()?.sync_primary_project_team),
      organizationalAccess: computed(() => this.dataset()?.organizational_access),
      orgName: this.dataset()?.storage_organization?.full_name,
    };
  }
  datasetPermissions = computed(() => {
    if (!this.dataset()) return [];
    return mapUsersToPermissionRows(this.dataset().dataset_users, this.datasetPermissionContext, DatasetRoleOptions);
  });
  readonly useEmailDropdown = computed(() => this.availablePermissionEmails().length > 0);

  availablePermissionEmails = signal<string[]>([]);
  private datasetsService!: DatasetsService;
  visibilityForm: FormGroup;
  fileVisibilityForm: FormGroup;
  canMakeFilesPublic = signal(true);
  protected readonly pdeRoutes = pdeRoutes;

  syncProgress: WritableSignal<SyncProgress> = signal(undefined);

  private readonly subscriptions: Subscription[] = [];

  constructor(
    activatedRoute: ActivatedRoute,
    landingServiceBroker: LandingServiceBrokerService,
    landingServiceConfig: LandingServiceConfigService,
    private readonly indexService: IndexService,
    fb: FormBuilder,
    private readonly snackbarService: MatSnackBar,
    private readonly dialogService: MatDialog,
    private readonly router: Router,
    private readonly authService: AuthenticationService,
    private readonly pubFileService: PublicDatasetFileService,
  ) {
    let isSetup = false;
    this.visibilityForm = fb.group({
      visibility: '',
    });
    this.fileVisibilityForm = fb.group({
      visibility: '',
    });

    this.subscriptions.push(
      activatedRoute.data.subscribe(d => {
        const dataset = d['dataset'] as DatasetWithPermissionEmails;
        this.dataset.set(dataset);

        this.availablePermissionEmails.set(dataset.availablePermissionEmails ?? []);

        const orgName = dataset.storage_organization?.name ?? '';
        landingServiceBroker.getByOrganizationName(orgName).then(services => {
          this.datasetsService = services.DatasetsService;
        });

        landingServiceConfig
          .getPromise(orgName)
          .then(config =>
            this.canMakeFilesPublic.set(
              config.allow_dataset_data_public &&
                (dataset.dataset_file_versions.length > 0 || config.allow_file_uploads),
            ),
          );

        if (!isSetup) {
          isSetup = true;
          this.visibilityForm
            .get('visibility')
            .setValue(dataset.visible_to_all_users ? 'public' : 'team', {emitEvent: false});
          this.fileVisibilityForm
            .get('visibility')
            .setValue(dataset.is_data_public ? 'public' : 'team', {emitEvent: false});
          this.subscriptions.push(
            this.visibilityForm.valueChanges.subscribe(newValue =>
              this.changeDatasetMetaVisibility(newValue.visibility == 'public'),
            ),
          );
          this.subscriptions.push(
            this.fileVisibilityForm.valueChanges.subscribe(newValue =>
              this.changeDatasetFileVisibility(newValue.visibility == 'public'),
            ),
          );
        }
      }),
    );
  }

  public sectionHeaderActions = signal([
    {
      target: '/help/private-data-enclave/edit-private-dataset-access',
      targetTab: '_blank',
      caption: 'Help',
      color: 'var(--mat-sys-primary)',
      icon: 'help_outline',
    },
  ]);

  ngOnDestroy(): void {
    for (let sub of this.subscriptions) {
      sub.unsubscribe();
    }
  }

  async changeDatasetMetaVisibility(isPublic: boolean) {
    await this.actOnDataset({
      action: () =>
        lastValueFrom(
          this.datasetsService.patchDataset(this.dataset().id, {
            dataset_type: this.dataset().dataset_type,
            visible_to_all_users: isPublic,
          }),
        ),
      successDescription: `Dataset metadata made ${isPublic ? 'available' : 'unavailable'} on the Commons.`,
      failureDescription: `Could not make dataset metadata ${isPublic ? 'public' : 'private'}`,
      rollbackAction: async () => {
        const control = this.visibilityForm.get('visibility');
        control.setValue(control.value == 'public' ? 'team' : 'public', {emitEvent: false});
      },
    });
  }

  async changeDatasetFileVisibility(isPublic: boolean) {
    try {
      if (isPublic) {
        this.syncProgress.set({isProcessingMetadata: true});
      }
      await this.actOnDataset({
        action: async () => {
          await lastValueFrom(
            this.datasetsService.patchDataset(this.dataset().id, {
              dataset_type: this.dataset().dataset_type,
              is_data_public: isPublic,
            }),
          );
          await lastValueFrom(
            this.indexService.syncDatasetInIndex(this.dataset().id, {
              storage_organization: this.dataset().storage_organization.name,
            }),
          );
          if (isPublic) {
            const fileVersionInfo = this.dataset().dataset_file_versions.find(
              dfv => dfv.is_latest_and_not_deleted,
            ).file_version;
            const needsSyncback = !this.dataset().doi;
            let tries = 0;
            while (!this.dataset().doi) {
              await new Promise(resolve => setTimeout(resolve, 1000));
              this.dataset.set(
                (
                  await lastValueFrom(
                    this.indexService.searchDatasetsInIndex(
                      {id: {terms: this.dataset().id}},
                      {public_view: false, page: 1, per_page: 1},
                    ),
                  )
                ).results[0] as unknown as CommonsSearchDataset,
              );
              if (++tries > 20) break;
            }
            if (!this.dataset().doi) {
              throw new Error('Could not get a DOI from Libradata');
            }
            if (needsSyncback) {
              await lastValueFrom(
                this.datasetsService.patchDataset(this.dataset().id, {
                  dataset_type: this.dataset().dataset_type,
                  doi: this.dataset().doi,
                }),
              );
            }
            this.syncProgress.set({downloaded: 0, uploaded: 0, total: fileVersionInfo.file_size});
            if (fileVersionInfo.file_size < CHUNK_LIMIT) {
              await this.fullUpload(fileVersionInfo);
            } else {
              await this.chunkedUpload(fileVersionInfo);
            }
          }
        },
        successDescription: `The newest dataset file has been made ${isPublic ? 'available' : 'unavailable'} on the Public Data Commons.`,
        failureDescription: `Could not make the dataset file ${isPublic ? 'public' : 'private'}`,
        suppressSync: true,
        rollbackAction: async () => {
          const control = this.fileVisibilityForm.get('visibility');
          control.setValue(control.value == 'public' ? 'team' : 'public', {emitEvent: false});
        },
      });
    } finally {
      this.syncProgress.set(undefined);
    }
  }

  /// File is <512MB, perform regular download/upload. Because of *waves hands* the state of the web, this has to download the entire file before beginning to upload any of it.
  async fullUpload(fileVersionInfo: FileVersionResponse) {
    const [downXhr, downPromise] = this.XHR();
    const [upXhr, upPromise] = this.XHR();
    downXhr.addEventListener('progress', ev =>
      this.syncProgress.update(prev => ({...prev, downloaded: ev.loaded, total: ev.total})),
    );
    downXhr.open(
      'GET',
      `${this.datasetsService.baseUrl}/datasets/${this.dataset().id}/files/latest?download_stored_format=true`,
      true,
    );
    downXhr.setRequestHeader('Authorization', `Bearer ${this.authService.token()}`);
    downXhr.responseType = 'blob';
    downXhr.send(null);
    await downPromise;
    const fileData: Blob = downXhr.response;
    upXhr.upload.addEventListener('progress', ev =>
      this.syncProgress.update(prev => ({...prev, uploaded: ev.loaded, isFinalizing: ev.loaded == ev.total})),
    );
    upXhr.open(
      'POST',
      `${this.pubFileService.baseUrl}/api/public-dataset-file?dataset_id=${this.dataset().id}&storage_organization=${this.dataset().storage_organization.name}`,
    );
    upXhr.setRequestHeader('Authorization', `Bearer ${this.authService.token()}`);
    const formData = new FormData();
    formData.append('file', new File([await fileData.arrayBuffer()], fileVersionInfo.file_name));
    upXhr.send(formData);
    await upPromise;
  }

  /// File is >=512MB, perform download/upload in chunks.
  async chunkedUpload(fileVersionInfo: FileVersionResponse) {
    this.syncProgress.update(prev => ({...prev, isChunked: true}));
    const downFetch = await fetch(
      `${this.datasetsService.baseUrl}/datasets/${this.dataset().id}/files/latest?download_stored_format=true`,
      {headers: {Authorization: `Bearer ${this.authService.token()}`}},
    );
    if (!downFetch.ok) {
      throw new HttpErrorResponse({
        error: downFetch.json(),
        headers: new HttpHeaders(downFetch.headers),
        status: downFetch.status,
        statusText: downFetch.statusText,
        url: downFetch.url,
        redirected: downFetch.redirected,
      });
    }
    const uploadId = crypto.randomUUID();
    let chunk = 1;
    let pendingLength = 0;
    let uploadedTotal = 0;
    let prevUpPromise: Promise<void> | undefined;
    let blobParts = [];
    // *** Ideally `body` could be passed directly as the request body of the upload
    // and we could skip all of the faffing about with chunks and all that
    // but that's barely supported in Chrome, not supported in Firefox or Safari,
    // barely supported in some HTTP/2 servers, and not supported in any servers we use.
    // Also, we can't calculate progress that way.
    const downReader = downFetch.body.getReader();
    while (true) {
      const {done, value} = await downReader.read();
      if (!done) {
        this.syncProgress.update(prev => ({...prev, downloaded: prev.downloaded + value.length}));
        blobParts.push(value);
        const newLength = pendingLength + value.length;
        if (newLength > CHUNK_SIZE) {
          // We've downloaded 64MB, time to upload it
          const [upXhr, upPromise] = this.XHR();
          upXhr.open(
            'POST',
            `${this.pubFileService.baseUrl}/api/public-dataset-file/chunked-upload?dataset_id=${this.dataset().id}&storage_organization=${this.dataset().storage_organization.name}&upload_id=${uploadId}&chunk_number=${chunk++}&finalize=false`,
            true,
          );
          upXhr.setRequestHeader('Authorization', `Bearer ${this.authService.token()}`);
          const syncProgress = this.syncProgress;
          (function () {
            // Capture current value before it changes
            const thisBlobTotal = uploadedTotal;
            upXhr.upload.addEventListener('progress', ev =>
              syncProgress.update(prev => ({...prev, uploaded: thisBlobTotal + ev.loaded})),
            );
          })();
          const formData = new FormData();
          formData.append('file', new File(blobParts, fileVersionInfo.file_name));
          blobParts = [];
          pendingLength = 0;
          if (prevUpPromise) await prevUpPromise;
          prevUpPromise = upPromise;
          upXhr.send(formData);
          uploadedTotal += newLength;
        } else {
          pendingLength += value.length;
        }
      } else {
        // Drain any remaining chunks.
        const [upXhr, upPromise] = this.XHR();
        upXhr.open(
          'POST',
          `${this.pubFileService.baseUrl}/api/public-dataset-file/chunked-upload?dataset_id=${this.dataset().id}&storage_organization=${this.dataset().storage_organization.name}&upload_id=${uploadId}&chunk_number=${chunk}&finalize=true`,
          true,
        );
        upXhr.setRequestHeader('Authorization', `Bearer ${this.authService.token()}`);
        upXhr.upload.addEventListener('progress', ev =>
          this.syncProgress.update(prev => ({
            ...prev,
            uploaded: uploadedTotal + ev.loaded,
            isFinalizing: ev.loaded == ev.total,
          })),
        );
        const formData = new FormData();
        formData.append('file', new Blob(blobParts));
        if (prevUpPromise) await prevUpPromise;
        upXhr.send(formData);
        await upPromise;
        break;
      }
    }
  }

  XHR(): [XMLHttpRequest, Promise<void>] {
    const xhr = new XMLHttpRequest();
    let resolve, reject;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    xhr.addEventListener('readystatechange', ev => {
      if (xhr.readyState == XMLHttpRequest.DONE) {
        if (xhr.status >= 200 && xhr.status < 299) {
          resolve();
        } else {
          reject(
            new HttpErrorResponse({
              error: xhr.response, //TODO: JSON parse this?
              headers: new HttpHeaders(xhr.getAllResponseHeaders()),
              status: xhr.status,
              statusText: xhr.statusText,
              url: xhr.responseURL,
              redirected: false,
            }),
          );
        }
      }
    });
    return [xhr, promise];
  }

  async deleteDataset() {
    if (
      !(await lastValueFrom(
        this.dialogService
          .open(DeleteDialogComponent, {data: {objectName: this.dataset().name, objectKind: 'dataset'}})
          .afterClosed(),
      ))
    )
      return;
    await this.actOnDataset({
      action: () => lastValueFrom(this.datasetsService.deleteDataset(this.dataset().id, {immediate: false})),
      successDescription: 'Dataset deleted.',
      failureDescription: 'Could not delete dataset',
      postAction: async () => {
        await this.router.navigateByUrl('/private-data-enclave');
      },
    });
  }

  async modifyPermission(row: PermissionRow) {
    const dataset = this.dataset();
    let user = dataset.dataset_users.find(u => u.user.email == row.email);
    if (!user) {
      user = <DatasetUserResponse>(<unknown>{user: {email: row.email}});
      dataset.dataset_users.push(user);
    }
    user.dataset_role_id = row.roleId;
    await this.actOnDataset({
      action: () =>
        lastValueFrom(
          this.datasetsService.patchDataset(dataset.id, {
            dataset_type: dataset.dataset_type,
            users: dataset.dataset_users.map(u => ({email: u.user.email, role: u.dataset_role_id as DatasetRoleID})),
          }),
        ),
      successDescription: 'Permissions updated.',
      failureDescription: 'Could not update permissions',
    });
    this.dataset.set(await lastValueFrom(this.datasetsService.getDataset(dataset.id)));
  }

  async revokePermission(row: PermissionRow) {
    const dataset = this.dataset();
    await this.actOnDataset({
      action: () =>
        lastValueFrom(
          this.datasetsService.patchDataset(dataset.id, {
            dataset_type: dataset.dataset_type,
            users: dataset.dataset_users
              .filter(u => u.user.email != row.email)
              .map(u => ({email: u.user.email, role: u.dataset_role_id as DatasetRoleID})),
          }),
        ),
      successDescription: 'Permissions updated.',
      failureDescription: 'Could not update permissions',
    });
    this.dataset.set(await lastValueFrom(this.datasetsService.getDataset(dataset.id)));
  }

  async modifyPermissionSync(newState: boolean) {
    const dataset = this.dataset();
    await this.actOnDataset({
      action: () =>
        lastValueFrom(
          this.datasetsService.patchDataset(dataset.id, {
            dataset_type: dataset.dataset_type,
            sync_primary_project_team: newState,
          }),
        ),
      successDescription: 'Permissions updated.',
      failureDescription: 'Could not update permissions',
    });
    this.dataset.set(await lastValueFrom(this.datasetsService.getDataset(dataset.id)));
  }

  async modifyOrgAccess(newState: boolean) {
    const dataset = this.dataset();
    await this.actOnDataset({
      action: () =>
        lastValueFrom(
          this.datasetsService.patchDataset(dataset.id, {
            dataset_type: dataset.dataset_type,
            organizational_access: newState,
          }),
        ),
      successDescription: 'Permissions updated.',
      failureDescription: 'Could not update permissions',
    });
    this.dataset.set(await lastValueFrom(this.datasetsService.getDataset(dataset.id)));
  }

  async actOnDataset(params: {
    action: () => Promise<void>;
    successDescription: string;
    failureDescription: string;
    postAction?: () => Promise<void>;
    suppressSync?: boolean;
    rollbackAction?: () => Promise<void>;
  }): Promise<void> {
    const {action, successDescription, failureDescription, postAction, suppressSync, rollbackAction} = params;
    let success = false;
    try {
      await action();
      if (!suppressSync)
        await lastValueFrom(
          this.indexService.syncDatasetInIndex(this.dataset().id, {
            storage_organization: this.dataset().storage_organization.name,
          }),
        );
      success = true;
    } catch (ex) {
      const her = ex as HttpErrorResponse;
      if (her.status === 403 && Array.isArray(her.error['detail']?.['options'])) {
        this.dialogService.open(FilePublicationValidationDialogComponent, {
          data: {isSpeculation: false, isDirect: true, error: her},
        });
      } else {
        this.snackbarService.open(`${failureDescription}: ${extractError(<HttpErrorResponse>ex)}`, undefined, {
          duration: 15000,
          panelClass: 'snackbar-error',
        });
      }
      if (rollbackAction) {
        await rollbackAction();
      }
    }
    if (success) {
      this.snackbarService.open(successDescription, undefined, {duration: 15000});
      if (postAction) {
        await postAction();
      }
    }
  }
}
